import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLiveQuery } from 'dexie-react-hooks';
import { createColumnHelper, flexRender, getCoreRowModel, getSortedRowModel, useReactTable, type Row, type SortingState } from '@tanstack/react-table';
import { useVirtualizer } from '@tanstack/react-virtual';
import { daysBetween, type Contact, type Person, type SchemaOptions, type User } from '@crmsy/shared';
import { todayFor, usePerspective } from '../lib/viewAs.js';
import { db, getMeta, setMeta } from '../lib/db.js';
import { applyFilters } from '../lib/filters.js';
import { flattenGroups, groupItems, toggleCollapsed } from '../lib/grouping.js';
import { PRIORITY, useKeys } from '../lib/keyboard.js';
import { onContactRenamed } from '../lib/actions.js';
import { useListFilters } from '../lib/useListFilters.jsx';
import { useRecordActions, type RecordDialog } from '../lib/useRecordActions.jsx';
import { FilterChips } from '../components/FilterChips.jsx';
import { GroupBar, GroupToggle } from '../components/GroupBar.jsx';
import { GroupHeader } from '../components/GroupHeader.jsx';
import { Picker } from '../components/Picker.jsx';
import { ContactPanel } from '../components/ContactPanel.jsx';
import { Owners } from '../components/Avatar.jsx';
import { Tag } from '../components/Tag.jsx';
import { t, useT, type Key } from '../i18n/index.js';

const col = createColumnHelper<Contact>();

export interface TableViewProps {
  user: User;
  search: string;
  setSearch: (q: string) => void;
}

function DueCell({ date, today }: { date: string | null; today: string }) {
  if (!date) return <span className="text-muted">—</span>;
  const d = daysBetween(today, date);
  const cls = d < 0 ? 'text-danger font-medium' : d === 0 ? 'text-warn-ink font-medium' : '';
  const hint = d < 0 ? t('due.late', { n: -d }) : d === 0 ? t('due.today') : t('due.short', { n: d });
  return (
    <span className={cls}>
      {date} <span className="text-[11px] opacity-70">{hint}</span>
    </span>
  );
}

const EDITABLE_COLUMNS: Record<string, RecordDialog> = { stage: 'stage', status: 'status', nextAction: 'nextAction', nextContactDate: 'date', owner: 'owner' };

/** §6.3 default columns; every other canonical field and every `extra` Notion property can be switched on. */
export const DEFAULT_COLUMNS = ['companyName', 'contact', 'stage', 'status', 'nextAction', 'owner', 'nextContactDate', 'lastContactDate', 'leadSource'];
const OPTIONAL_FIELDS: { id: keyof Contact; header: Key; size: number }[] = [
  { id: 'contactRole', header: 'field.role', size: 150 },
  { id: 'email', header: 'field.email', size: 200 },
  { id: 'phone', header: 'field.phone', size: 140 },
  { id: 'linkedin', header: 'field.linkedin', size: 180 },
  { id: 'companySite', header: 'field.site', size: 180 },
  { id: 'industry', header: 'field.industry', size: 160 },
  { id: 'createdTime', header: 'field.created', size: 110 },
  { id: 'lastEditedTime', header: 'field.edited', size: 110 },
];
const EXTRA_PREFIX = 'extra:';

interface TablePrefs {
  columns: string[];
  sorting: SortingState;
}
const prefsKey = (userId: string) => `table:${userId}`;

/** Table (§6.3): everyone's contacts, filter chips, fuzzy search, keyboard navigation, inline edits on the selected row. */
export function TableView({ user, search, setSearch }: TableViewProps) {
  const t = useT();
  const contacts = useLiveQuery(() => db.contacts.toArray(), [], undefined);
  const people = useLiveQuery(() => db.people.toArray(), [], [] as Person[]);
  const schemaRows = useLiveQuery(() => db.schema.toArray(), [], []);
  const schema = useMemo<SchemaOptions>(() => Object.fromEntries(schemaRows.map((r) => [r.property, r.options])), [schemaRows]);
  const pendingIds = useLiveQuery(async () => new Set((await db.outbox.toArray()).map((o) => o.contactId)), [], new Set<string | null>());

  const lf = useListFilters({ search, setSearch, contacts, people, schema, view: 'table' });
  const { filters, setFilters, groups, setGroups } = lf;

  const me = usePerspective(user);
  const today = todayFor(me);
  const rows = useMemo(
    () => (contacts ? applyFilters(contacts, filters, { today, myNotionId: me.notionUserId }) : []),
    [contacts, filters, today, me.notionUserId],
  );

  // Column picker + sort, persisted per user in `meta` (§6.3).
  const [prefs, setPrefs] = useState<TablePrefs | null>(null);
  useEffect(() => {
    void getMeta<TablePrefs>(prefsKey(user.id)).then((p) => setPrefs(p ?? { columns: DEFAULT_COLUMNS, sorting: [{ id: 'nextContactDate', desc: false }] }));
  }, [user.id]);
  const savePrefs = useCallback(
    (next: TablePrefs) => {
      setPrefs(next);
      void setMeta(prefsKey(user.id), next);
    },
    [user.id],
  );
  const sorting = prefs?.sorting ?? [];
  const setSorting = useCallback(
    (updater: SortingState | ((s: SortingState) => SortingState)) => {
      if (!prefs) return;
      savePrefs({ ...prefs, sorting: typeof updater === 'function' ? updater(prefs.sorting) : updater });
    },
    [prefs, savePrefs],
  );
  const visible = prefs?.columns ?? DEFAULT_COLUMNS;
  const [columnPicker, setColumnPicker] = useState<{ x: number; y: number } | null | false>(false);
  const extraKeys = useMemo(() => {
    const keys = new Set<string>();
    for (const c of contacts ?? []) for (const k of Object.keys(c.extra)) keys.add(k);
    return [...keys].sort();
  }, [contacts]);

  const [selected, setSelected] = useState(0);
  const [panelId, setPanelId] = useState<string | null>(null);

  useEffect(() => onContactRenamed((t, r) => setPanelId((p) => (p === t ? r : p))), []);

  const columns = useMemo(
    () => [
      col.accessor('companyName', {
        header: t('field.company'),
        cell: (c) => (
          <span className="font-medium">
            {pendingIds.has(c.row.original.id) && <span className="mr-1.5 inline-block h-1.5 w-1.5 rounded-full bg-warn-ink align-middle" title={t('panel.saving')} />}
            {c.getValue() || <span className="text-muted">{t('panel.noCompany')}</span>}
          </span>
        ),
        size: 220,
      }),
      col.accessor('contact', { header: t('field.contact'), size: 170 }),
      col.accessor('stage', { header: t('field.stage'), cell: (c) => <Tag schema={schema} property="Stage" value={c.getValue()} />, size: 130 }),
      col.accessor('status', { header: t('field.status'), cell: (c) => <Tag schema={schema} property="Status" value={c.getValue()} />, size: 150 }),
      col.accessor('nextAction', { header: t('field.nextAction'), cell: (c) => <Tag schema={schema} property="Next Action" value={c.getValue()} extra={c.row.original.nextActionExtra.length} />, size: 150 }),
      col.accessor('owner', { header: t('field.owner'), cell: (c) => <Owners ids={c.getValue()} people={people} />, size: 170, sortingFn: (a, b) => (people.find((p) => p.id === a.original.owner[0])?.name ?? '').localeCompare(people.find((p) => p.id === b.original.owner[0])?.name ?? '') }),
      col.accessor('nextContactDate', { header: t('field.nextContact'), cell: (c) => <DueCell date={c.getValue()} today={today} />, size: 150, sortUndefined: 'last', sortingFn: (a, b) => (a.original.nextContactDate ?? '9999').localeCompare(b.original.nextContactDate ?? '9999') }),
      col.accessor('lastContactDate', { header: t('field.lastContact'), cell: (c) => c.getValue() ?? <span className="text-muted">—</span>, size: 120, sortingFn: (a, b) => (a.original.lastContactDate ?? '').localeCompare(b.original.lastContactDate ?? '') }),
      col.accessor('leadSource', { header: t('field.leadSource'), cell: (c) => <Tag schema={schema} property="Lead Source" value={c.getValue()} />, size: 140 }),
      ...OPTIONAL_FIELDS.map((f) =>
        col.accessor((row) => row[f.id] as string | null, {
          id: f.id,
          header: t(f.header),
          size: f.size,
          cell: (c) => {
            const v = c.getValue();
            if (!v) return <span className="text-muted">—</span>;
            if (f.id === 'createdTime' || f.id === 'lastEditedTime') return v.slice(0, 10);
            if (f.id === 'linkedin' || f.id === 'companySite') return <a className="text-accent-dark hover:underline" href={v.startsWith('http') ? v : `https://${v}`} target="_blank" rel="noopener">{v}</a>;
            if (f.id === 'contactRole' || f.id === 'industry') return <Tag schema={schema} property={f.id === 'contactRole' ? 'Contact Role' : 'Industry'} value={v} />;
            return v;
          },
        }),
      ),
      ...extraKeys.map((k) =>
        col.accessor((row) => row.extra[k] ?? null, {
          id: EXTRA_PREFIX + k,
          header: k,
          size: 160,
          cell: (c) => {
            const v = c.getValue();
            if (v === null || v === '' || v === false || (Array.isArray(v) && !v.length)) return <span className="text-muted">—</span>;
            return Array.isArray(v) ? v.join(', ') : String(v);
          },
          sortingFn: (a, b) => String(a.getValue(EXTRA_PREFIX + k) ?? '').localeCompare(String(b.getValue(EXTRA_PREFIX + k) ?? '')),
        }),
      ),
    ],
    [schema, people, today, pendingIds, extraKeys, t],
  );

  const columnVisibility = useMemo(() => Object.fromEntries(columns.map((c) => [c.id!, visible.includes(c.id!)])), [columns, visible]);
  const columnOptions = useMemo(() => columns.map((c) => ({ value: c.id!, label: typeof c.header === 'string' ? c.header : c.id! })), [columns]);
  const openColumnPicker = useCallback((anchor: { x: number; y: number } | null = null) => {
    if (!anchor) {
      const r = document.querySelector<HTMLElement>('[data-column-picker]')?.getBoundingClientRect();
      if (r) anchor = { x: Math.max(16, r.right - 288), y: r.bottom + 6 };
    }
    setColumnPicker((p) => (p === false ? anchor : false));
  }, []);

  const table = useReactTable({
    data: rows,
    columns,
    state: { sorting, columnVisibility },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
    enableSortingRemoval: false,
  });
  const sortedRows = table.getRowModel().rows;

  // D-38: the column sort orders rows inside each group; headers are rows of the same virtual list.
  const [collapsed, setCollapsed] = useState<Set<string>>(() => new Set());
  const tree = useMemo(() => groupItems(sortedRows, groups, (r: Row<Contact>) => r.original, { schema, people, today }), [sortedRows, groups, schema, people, today]);
  const flat = useMemo(() => flattenGroups(tree, sortedRows, collapsed), [tree, sortedRows, collapsed]);
  const itemRows = useMemo(() => flat.flatMap((r, i) => (r.kind === 'item' ? [{ row: r.item, flatIndex: i, path: r.path }] : [])), [flat]);
  const indexOfFlat = useMemo(() => new Map(itemRows.map((r, i) => [r.flatIndex, i])), [itemRows]);

  const scrollRef = useRef<HTMLDivElement>(null);
  const virtualizer = useVirtualizer({
    count: flat.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: (i) => (flat[i]?.kind === 'header' ? 32 : 40),
    // Measured heights follow the row, not its position: collapsing a group shifts every index below it.
    getItemKey: (i) => (flat[i]!.kind === 'header' ? (flat[i] as { node: { path: string } }).node.path : (flat[i] as { item: Row<Contact> }).item.id),
    overscan: 12,
  });

  useEffect(() => {
    setSelected((s) => Math.min(s, Math.max(0, itemRows.length - 1)));
  }, [itemRows.length]);

  useEffect(() => {
    const fi = itemRows[selected]?.flatIndex;
    if (fi !== undefined) virtualizer.scrollToIndex(fi, { align: 'auto' });
  }, [selected, itemRows, virtualizer]);

  const current = itemRows[selected]?.row.original ?? null;
  const panelContact = panelId ? (contacts?.find((c) => c.id === panelId) ?? null) : null;

  // f / z / c act on the selected row (§7 list context); cell clicks open the same pickers as the panel.
  const ra = useRecordActions(current, { mode: 'list', priority: PRIORITY.view, enabled: !panelContact, schema, people, user });

  useKeys(
    {
      j: () => setSelected((s) => Math.min(s + 1, itemRows.length - 1)),
      k: () => setSelected((s) => Math.max(s - 1, 0)),
      ArrowDown: () => setSelected((s) => Math.min(s + 1, itemRows.length - 1)),
      ArrowUp: () => setSelected((s) => Math.max(s - 1, 0)),
      h: () => {
        const path = itemRows[selected]?.path;
        if (path) setCollapsed((c) => toggleCollapsed(c, path));
      },
      Enter: () => {
        if (current) setPanelId(current.id);
      },
      'Shift+Enter': () => {
        if (current?.url) window.open(current.url, '_blank', 'noopener');
      },
      ...lf.keys,
      v: () => openColumnPicker(),
      Escape: () => {
        if (ra.dialog) return false;
        if (columnPicker !== false) {
          setColumnPicker(false);
          return;
        }
        if (!lf.escape()) return false;
      },
    },
    { priority: PRIORITY.view, enabled: !panelContact },
  );

  const onCellClick = useCallback(
    (e: React.MouseEvent, index: number, columnId: string) => {
      const kind = EDITABLE_COLUMNS[columnId];
      if (!kind || index !== selected) return;
      e.stopPropagation();
      const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
      ra.open(kind, { x: r.left, y: r.bottom + 2 });
    },
    [selected, ra],
  );

  if (contacts === undefined) {
    return <div className="p-6 text-sm text-muted">{t('table.opening')}</div>;
  }

  return (
    <div className="flex h-full flex-col">
      <div className="flex flex-wrap items-center gap-2 border-b border-line bg-paper px-4 py-2">
        <FilterChips filters={filters} setFilters={setFilters} schema={schema} people={people} openPicker={lf.openPicker} />
        <GroupToggle open={lf.barOpen} onToggle={lf.toggleBar} groups={groups} />
        <span className="ml-auto text-xs text-muted">
          {rows.length === contacts.length ? t('table.contacts', { n: rows.length }) : t('table.of', { n: rows.length, total: contacts.filter((c) => !c.archived).length })}
        </span>
        <button
          type="button"
          className="chip"
          data-column-picker
          data-on={columnPicker !== false}
          onClick={(e) => {
            const r = (e.currentTarget as HTMLElement).getBoundingClientRect();
            openColumnPicker({ x: Math.max(16, r.right - 288), y: r.bottom + 6 });
          }}
        >
          <kbd>v</kbd>
          {t('table.columns')}
        </button>
      </div>
      {lf.barOpen && <GroupBar groups={groups} setGroups={setGroups} />}

      {contacts.length === 0 ? (
        <div className="p-6 text-sm text-muted">{t('table.empty')}</div>
      ) : (
        <div ref={scrollRef} className="min-h-0 flex-1 overflow-auto">
          <table className="w-full min-w-[1100px] border-separate border-spacing-0 text-sm" style={{ tableLayout: 'fixed' }}>
            <thead className="sticky top-0 z-10 bg-surface">
              {table.getHeaderGroups().map((hg) => (
                <tr key={hg.id}>
                  {hg.headers.map((h) => (
                    <th
                      key={h.id}
                      style={{ width: h.getSize() }}
                      onClick={h.column.getToggleSortingHandler()}
                      className="cursor-pointer select-none border-b border-line px-3 py-2 text-left text-xs font-semibold uppercase tracking-wide text-muted hover:text-ink"
                    >
                      {flexRender(h.column.columnDef.header, h.getContext())}
                      {{ asc: ' ↑', desc: ' ↓' }[h.column.getIsSorted() as string] ?? ''}
                    </th>
                  ))}
                </tr>
              ))}
            </thead>
            <tbody style={{ height: virtualizer.getTotalSize(), position: 'relative', display: 'block' }}>
              {virtualizer.getVirtualItems().map((vi) => {
                const fr = flat[vi.index]!;
                if (fr.kind === 'header') {
                  return (
                    <tr key={fr.node.path} data-index={vi.index} ref={virtualizer.measureElement} className="absolute left-0 flex w-full items-center bg-surface" style={{ transform: `translateY(${vi.start}px)` }}>
                      <td className="w-full px-3 py-1.5">
                        <GroupHeader node={fr.node} collapsed={fr.collapsed} schema={schema} onToggle={() => setCollapsed((c) => toggleCollapsed(c, fr.node.path))} />
                      </td>
                    </tr>
                  );
                }
                const row = fr.item;
                const index = indexOfFlat.get(vi.index) ?? -1;
                const isSel = index === selected;
                return (
                  <tr
                    key={row.id}
                    data-index={vi.index}
                    ref={virtualizer.measureElement}
                    onClick={() => setSelected(index)}
                    onDoubleClick={() => setPanelId(row.original.id)}
                    className={`absolute left-0 flex w-full cursor-default items-center border-b border-line bg-paper hover:bg-surface ${isSel ? 'row-selected' : ''}`}
                    style={{ transform: `translateY(${vi.start}px)` }}
                  >
                    {row.getVisibleCells().map((cell) => (
                      <td
                        key={cell.id}
                        style={{ width: cell.column.getSize() }}
                        onClick={(e) => onCellClick(e, index, cell.column.id)}
                        className={`shrink-0 truncate px-3 py-2 ${isSel && EDITABLE_COLUMNS[cell.column.id] ? 'cursor-pointer' : ''}`}
                      >
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                      </td>
                    ))}
                  </tr>
                );
              })}
            </tbody>
          </table>
          {rows.length === 0 && <div className="p-6 text-sm text-muted">{t('table.noMatch')}</div>}
        </div>
      )}

      {lf.pickerElement}
      {columnPicker !== false && prefs && (
        <Picker
          title={t('table.columns')}
          options={columnOptions}
          selected={visible}
          multi
          onToggle={(id) => {
            const next = visible.includes(id) ? visible.filter((v) => v !== id) : [...visible, id];
            if (next.length) savePrefs({ ...prefs, columns: next });
          }}
          onClose={() => setColumnPicker(false)}
          anchor={columnPicker}
        />
      )}
      {ra.element}
      {panelContact && <ContactPanel contact={panelContact} people={people} schema={schema} user={user} onClose={() => setPanelId(null)} />}
    </div>
  );
}
